import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { authGuard, noAuthGuard, roleGuard, onboardingGuard } from './core/auth/guards/auth.guard';
import { AuthService } from './core/auth/services/auth.service';

export const routes: Routes = [
  // Landing pública — solo cuando NO hay sesión activa
  {
    path: '',
    canMatch: [() => !inject(AuthService).isAuthenticated()],
    loadComponent: () => import('./modules/landing/landing.component').then(m => m.LandingComponent)
  },
  {
    path: 'registro',
    canActivate: [noAuthGuard],
    loadComponent: () => import('./modules/registro/registro.component').then(m => m.RegistroComponent)
  },
  // Páginas legales — públicas, sin guard
  {
    path: 'terminos',
    loadComponent: () => import('./modules/landing/components/landing-legal/terminos.component').then(m => m.TerminosComponent)
  },
  {
    path: 'privacidad',
    loadComponent: () => import('./modules/landing/components/landing-legal/privacidad.component').then(m => m.PrivacidadComponent)
  },
  {
    path: 'onboarding',
    canActivate: [authGuard],
    loadComponent: () => import('./modules/onboarding/onboarding-wizard.component').then(m => m.OnboardingWizardComponent)
  },
  {
    path: 'auth',
    canActivate: [noAuthGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./modules/auth/login/login.component').then(m => m.LoginComponent)
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' }
    ]
  },
  {
    path: '',
    canActivate: [authGuard, onboardingGuard],
    loadComponent: () => import('./shared/components/layout/shell/shell.component').then(m => m.ShellComponent),
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./modules/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'inventario',
        data: { roles: ['SuperAdmin', 'Admin', 'Supervisor', 'Almacenista'] },
        canActivate: [roleGuard],
        children: [
          {
            path: 'productos',
            loadComponent: () => import('./modules/inventario/productos/productos-list.component').then(m => m.ProductosListComponent)
          },
          {
            path: 'productos/nuevo',
            loadComponent: () => import('./modules/inventario/productos/producto-form.component').then(m => m.ProductoFormComponent)
          },
          {
            path: 'productos/:id/editar',
            loadComponent: () => import('./modules/inventario/productos/producto-form.component').then(m => m.ProductoFormComponent)
          },
          {
            path: 'lotes',
            loadComponent: () => import('./modules/inventario/lotes/lotes-por-vencer.component').then(m => m.LotesPorVencerComponent)
          },
          {
            path: 'lotes/:productoId',
            loadComponent: () => import('./modules/inventario/lotes/lotes-producto.component').then(m => m.LotesProductoComponent)
          },
          {
            path: 'categorias',
            loadComponent: () => import('./modules/inventario/categorias/categorias-list.component').then(m => m.CategoriasListComponent)
          },
          {
            path: 'fabricantes',
            loadComponent: () => import('./modules/inventario/fabricantes/fabricantes-list.component').then(m => m.FabricantesListComponent)
          },
          {
            path: 'movimientos',
            loadComponent: () => import('./modules/inventario/movimientos/movimientos-list.component').then(m => m.MovimientosListComponent)
          },
          {
            path: 'alertas',
            loadComponent: () => import('./modules/inventario/alertas/alertas-stock.component').then(m => m.AlertasStockComponent)
          },
          { path: '', redirectTo: 'productos', pathMatch: 'full' }
        ]
      },
      {
        path: 'facturacion',
        data: { roles: ['SuperAdmin', 'Admin', 'Supervisor', 'Cajero'] },
        canActivate: [roleGuard],
        children: [
          {
            path: 'facturas',
            loadComponent: () => import('./modules/facturacion/facturas/facturas-list.component').then(m => m.FacturasListComponent)
          },
          {
            path: 'facturas/nueva',
            loadComponent: () => import('./modules/facturacion/facturas/factura-form.component').then(m => m.FacturaFormComponent)
          },
          {
            path: 'facturas/:id',
            loadComponent: () => import('./modules/facturacion/facturas/factura-detail.component').then(m => m.FacturaDetailComponent)
          },
          {
            path: 'facturas/:facturaId/nota-credito',
            loadComponent: () => import('./modules/facturacion/notas-credito/nota-credito-form.component').then(m => m.NotaCreditoFormComponent)
          },
          {
            path: 'clientes',
            loadComponent: () => import('./modules/facturacion/clientes/clientes-list.component').then(m => m.ClientesListComponent)
          },
          {
            path: 'servicios',
            loadComponent: () => import('./modules/facturacion/servicios/servicios-list.component').then(m => m.ServiciosListComponent)
          },
          {
            path: 'notas-credito',
            loadComponent: () => import('./modules/facturacion/notas-credito/notas-credito-list.component').then(m => m.NotasCreditoListComponent)
          },
          { path: '', redirectTo: 'facturas', pathMatch: 'full' }
        ]
      },
      {
        path: 'reportes',
        data: { roles: ['SuperAdmin', 'Admin', 'Supervisor', 'Auditor'] },
        canActivate: [roleGuard],
        loadComponent: () => import('./modules/reportes/reportes.component').then(m => m.ReportesComponent)
      },
      {
        path: 'gastos',
        data: { roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
        canActivate: [roleGuard],
        loadComponent: () => import('./modules/gastos/gastos-list.component').then(m => m.GastosListComponent)
      },
      {
        path: 'compras',
        data: { roles: ['SuperAdmin', 'Admin', 'Supervisor'] },
        canActivate: [roleGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./modules/compras/compras-list.component').then(m => m.ComprasListComponent)
          },
          {
            path: 'nueva',
            loadComponent: () => import('./modules/compras/compra-form.component').then(m => m.CompraFormComponent)
          },
          {
            path: 'proveedores',
            loadComponent: () => import('./modules/compras/proveedores/proveedores-list.component').then(m => m.ProveedoresListComponent)
          },
          {
            path: ':id',
            loadComponent: () => import('./modules/compras/compra-detail.component').then(m => m.CompraDetailComponent)
          }
        ]
      },
      {
        path: 'mi-plan',
        data: { roles: ['SuperAdmin', 'Admin'] },
        canActivate: [roleGuard],
        loadComponent: () => import('./modules/mi-plan/mi-plan.component').then(m => m.MiPlanComponent)
      },
      {
        path: 'red-negocios',
        data: { roles: ['SuperAdmin', 'Admin'] },
        canActivate: [roleGuard],
        loadComponent: () => import('./modules/red-negocios/red-negocios.component').then(m => m.RedNegociosComponent)
      },
      {
        path: 'configuracion',
        data: { roles: ['SuperAdmin', 'Admin'] },
        canActivate: [roleGuard],
        children: [
          {
            path: 'empresa',
            loadComponent: () => import('./modules/configuracion/empresa/empresa.component').then(m => m.EmpresaComponent)
          },
          {
            path: 'usuarios',
            loadComponent: () => import('./modules/configuracion/usuarios/usuarios-list.component').then(m => m.UsuariosListComponent)
          },
          {
            path: 'roles',
            data: { roles: ['SuperAdmin'] },
            canActivate: [roleGuard],
            loadComponent: () => import('./modules/configuracion/roles/roles-list.component').then(m => m.RolesListComponent)
          },
          {
            path: 'numeracion-dian',
            loadComponent: () => import('./modules/configuracion/numeracion-dian/numeracion-dian.component').then(m => m.NumeracionDIANComponent)
          },
          {
            path: 'sucursales',
            loadComponent: () => import('./modules/configuracion/sucursales/sucursales-list.component').then(m => m.SucursalesListComponent)
          },
          {
            path: 'carga-masiva',
            loadComponent: () => import('./modules/configuracion/carga-masiva/carga-masiva.component').then(m => m.CargaMasivaComponent)
          },
          { path: '', redirectTo: 'empresa', pathMatch: 'full' }
        ]
      },
      {
        path: 'forbidden',
        loadComponent: () => import('./modules/forbidden/forbidden.component').then(m => m.ForbiddenComponent)
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  {
    path: 'super-admin',
    canActivate: [authGuard, roleGuard],
    data: { roles: ['SuperAdmin'] },
    loadComponent: () => import('./modules/super-admin/super-admin-shell.component').then(m => m.SuperAdminShellComponent),
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./modules/super-admin/dashboard/super-admin-dashboard.component').then(m => m.SuperAdminDashboardComponent)
      },
      {
        path: 'empresas',
        loadComponent: () => import('./modules/super-admin/empresas/super-admin-empresas.component').then(m => m.SuperAdminEmpresasComponent)
      },
      {
        path: 'empresas/:id',
        loadComponent: () => import('./modules/super-admin/empresa-detail/super-admin-empresa-detail.component').then(m => m.SuperAdminEmpresaDetailComponent)
      },
      {
        path: 'planes',
        loadComponent: () => import('./modules/super-admin/planes/super-admin-planes.component').then(m => m.SuperAdminPlanesComponent)
      },
      {
        path: 'sectores',
        loadComponent: () => import('./modules/super-admin/sectores/super-admin-sectores.component').then(m => m.SuperAdminSectoresComponent)
      },
      {
        path: 'configuracion',
        loadComponent: () => import('./modules/super-admin/configuracion/super-admin-configuracion.component').then(m => m.SuperAdminConfiguracionComponent)
      },
      {
        path: 'solicitudes',
        loadComponent: () => import('./modules/super-admin/solicitudes/super-admin-solicitudes.component').then(m => m.SuperAdminSolicitudesComponent)
      },
      {
        path: 'pagos',
        loadComponent: () => import('./modules/super-admin/pagos/super-admin-pagos.component').then(m => m.SuperAdminPagosComponent)
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  // Payment result page — public (no auth guard: user may return here after Wompi redirect)
  {
    path: 'pago/resultado',
    loadComponent: () => import('./modules/pago-resultado/pago-resultado.component').then(m => m.PagoResultadoComponent)
  },
  { path: '**', redirectTo: '' }
];
